import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { coursesAPI, instructorsAPI, statsAPI } from "../api/axios";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { usePublicLinks } from "../context/PublicLinksContext";

const REGISTER_URL =
  process.env.REACT_APP_REGISTER_URL || "https://wkf.ms/3V2FTWZ";

const COURSE_TYPE_LABELS = {
  coding: "Coding",
  data_science: "Data Science",
  cybersecurity: "Cybersecurity",
  pure_maths: "Pure Maths",
  physics: "Physics",
  accounting: "Accounting",
  chemistry: "Chemistry",
  life_sciences: "Life Sciences",
};

const COURSE_TYPE_COLORS = {
  coding: "bg-blue-100 text-blue-800",
  data_science: "bg-indigo-100 text-indigo-800",
  cybersecurity: "bg-rose-100 text-rose-800",
  pure_maths: "bg-purple-100 text-purple-800",
  physics: "bg-green-100 text-green-800",
  accounting: "bg-yellow-100 text-yellow-800",
  chemistry: "bg-red-100 text-red-800",
  life_sciences: "bg-teal-100 text-teal-800",
};

const Home = () => {
  const { links } = usePublicLinks();
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [courseRes, instructorRes, statsRes] = await Promise.all([
          coursesAPI.getAll({ enrollmentOpen: true }),
          instructorsAPI.getAll(),
          statsAPI.getPublic(),
        ]);
        setCourses(courseRes.data.courses.slice(0, 6));
        setInstructors(instructorRes.data.instructors || []);
        setStats(statsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div>
      {/* Hero Section with Video Background */}
      <section className="relative text-white py-20 lg:py-32 overflow-hidden flex items-center min-h-[85vh]">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          poster="/images/yatlandingposter.jpg"
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/images/yatlandingvideotwo.mp4" type="video/mp4" />
        </video>
        {/* Stronger gradient overlay for text readability — darker on the text side */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* Non-profit pill */}
            <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 bg-orange-500/20 border border-orange-400/50 text-orange-200 text-sm font-semibold rounded-full backdrop-blur-sm">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.366 2.446a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.539 1.118L10 14.347l-3.366 2.446c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.652 8.154c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
              </svg>
              Registered Non-profit · Cape Town
            </span>

            {/* Headline with accent on the action word */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-5 leading-tight tracking-tight">
              Empowering Young Africans Through{" "}
              <span className="text-orange-400">Technology</span>
            </h1>

            {/* Short, scannable subhead */}
            <p className="text-lg md:text-xl text-gray-100 mb-6 leading-relaxed max-w-2xl">
              A free non-profit program teaching tech and academic skills to
              high school and primary school students.
            </p>

            {/* Subject pills — what we actually teach */}
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                "Coding",
                "Robotics",
                "Data Science",
                "Cybersecurity",
                "Maths",
                "Physics",
                "Accounting",
              ].map((subject) => (
                <span
                  key={subject}
                  className="px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium rounded-full"
                >
                  {subject}
                </span>
              ))}
            </div>

            {/* Quick details — how/where we teach */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-8 text-sm text-gray-200">
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Online globally
              </span>
              <span className="w-1 h-1 bg-white/40 rounded-full" />
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                On-site in Khayelitsha
              </span>
              <span className="w-1 h-1 bg-white/40 rounded-full" />
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 16v-2m-8-6H2m18 0h2M5.636 5.636l-1.414-1.414m14.142 14.142l1.414 1.414M5.636 18.364l-1.414 1.414M19.778 5.636l1.414-1.414M12 18a6 6 0 100-12 6 6 0 000 12z" />
                </svg>
                In partnership with UCT
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={REGISTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg text-lg transition-colors"
              >
                Get Started
              </a>
              <Link
                to={links?.courses ? `/${links.courses}` : "/"}
                className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white border-2 border-white font-medium rounded-lg text-lg transition-colors"
              >
                View Courses
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 lg:py-24 bg-gradient-to-b from-white via-blue-50/40 to-white relative overflow-hidden">
        {/* Decorative background blobs */}
        <div className="absolute top-20 -left-32 w-72 h-72 bg-blue-200/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-32 -right-32 w-96 h-96 bg-orange-200/40 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Heading */}
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-xs font-bold rounded-full mb-5 tracking-widest uppercase">
              Our Programs
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-5 leading-tight text-gray-900">
              What We <span className="text-blue-600">Do</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Three free programs combining hands-on tech skills, academic
              tutoring, and after-school support — open to learners across
              South Africa and, online, from anywhere in the world.
            </p>
          </div>

          {/* Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Program 1: Tech Skills */}
            <div className="group relative bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border border-gray-100">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-600" />
              <div className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-blue-200/50 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                    High school
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.366 2.446a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.539 1.118L10 14.347l-3.366 2.446c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.652 8.154c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
                    </svg>
                    UCT partner
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug">
                  Coding, Robotics, Data Science & Cybersecurity
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  We teach high school students Python, web development, robotics,
                  data science and cybersecurity — equipping them with real,
                  employable tech skills.
                </p>
              </div>
            </div>

            {/* Program 2: Academic Tutoring */}
            <div className="group relative bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border border-gray-100">
              <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500" />
              <div className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-purple-200/50 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                    High school
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.366 2.446a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.539 1.118L10 14.347l-3.366 2.446c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.652 8.154c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
                    </svg>
                    UCT partner
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug">
                  Maths, Physics & Accounting
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Expert-led tutoring to help matric learners succeed in Pure
                  Maths, Physical Sciences, and Accounting.
                </p>
              </div>
            </div>

            {/* Program 3: Primary School Afterschool */}
            <div className="group relative bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden border border-gray-100">
              <div className="h-2 bg-gradient-to-r from-orange-500 to-red-500" />
              <div className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-orange-200/50 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full bg-orange-50 text-orange-700 border border-orange-100">
                    Primary school
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-rose-50 text-rose-700 border border-rose-100">
                    Khayelitsha
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug">
                  After-school Program
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  A free after-school program for primary school children in
                  Khayelitsha township — building strong foundations early.
                </p>
              </div>
            </div>
          </div>

          {/* Online + Onsite banner */}
          <div className="mt-14 relative rounded-3xl overflow-hidden bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 shadow-2xl">
            {/* Decorative blobs */}
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-orange-400/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative p-10 lg:p-12">
              <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                {/* Online */}
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl flex items-center justify-center shrink-0">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <span className="inline-block px-2.5 py-0.5 mb-2 text-xs font-bold tracking-wide rounded-full bg-orange-400 text-orange-900">
                      OPEN GLOBALLY
                    </span>
                    <h4 className="text-2xl font-bold text-white mb-2">
                      Online classes, anywhere
                    </h4>
                    <p className="text-blue-100 leading-relaxed">
                      Join our live online lessons from any country in the world —
                      all you need is a laptop and an internet connection.
                    </p>
                  </div>
                </div>

                {/* On-site */}
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl flex items-center justify-center shrink-0">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <span className="inline-block px-2.5 py-0.5 mb-2 text-xs font-bold tracking-wide rounded-full bg-blue-300 text-blue-900">
                      IN PERSON
                    </span>
                    <h4 className="text-2xl font-bold text-white mb-2">
                      On-site in Khayelitsha
                    </h4>
                    <p className="text-blue-100 leading-relaxed">
                      Learn face-to-face at our physical classroom in
                      Khayelitsha township, Cape Town.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats / At a Glance Section */}
      {stats && (
        <section className="py-5 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-orange-400">
                  290+
                </div>
                <p className="text-blue-200 text-sm mt-1">Graduated Students</p>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-orange-400">
                  130+
                </div>
                <p className="text-blue-200 text-sm mt-1">Active Students</p>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-orange-400">
                  {stats.totalCourses}+
                </div>
                <p className="text-blue-200 text-sm mt-1">Courses</p>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-orange-400">
                  {stats.volunteerInstructors}+
                </div>
                <p className="text-blue-200 text-sm mt-1">
                  Volunteer Instructors
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Courses Preview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Courses</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore our range of technology courses designed to equip you with
              the skills you need to succeed.
            </p>
          </div>

          {loading ? (
            <LoadingSpinner size="lg" className="py-12" />
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {courses.map((course) => {
                return (
                  <div
                    key={course._id}
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col"
                  >
                    <img
                      src={course.logo}
                      alt={course.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-6 flex flex-col flex-1">
                      <span
                        className={`inline-block px-3 py-1 text-xs font-medium rounded-full mb-3 self-start ${
                          COURSE_TYPE_COLORS[course.courseType] ||
                          "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {COURSE_TYPE_LABELS[course.courseType] ||
                          course.courseType}
                      </span>
                      <h3 className="text-xl font-semibold mb-2">
                        {course.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-2 flex-1">
                        {course.description}
                      </p>
                      <div className="flex items-center justify-between mt-auto pt-2">
                        <Link
                          to={course.linkToken ? `/${course.linkToken}` : "/"}
                          className="text-blue-600 font-medium hover:text-blue-700 text-sm"
                        >
                          Learn More
                        </Link>
                        <a
                          href={REGISTER_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                        >
                          Register
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="text-center mt-10">
            <Link
              to={links?.courses ? `/${links.courses}` : "/"}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              View All Courses
            </Link>
          </div>
        </div>
      </section>

      {/* Volunteer Instructors Section */}
      {instructors.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Our Volunteer Instructors
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Meet the dedicated professionals who volunteer their time and
                expertise to shape the next generation of African tech talent.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {instructors.map((instructor) => (
                <div key={instructor._id} className="bg-gray-50 rounded-xl overflow-hidden group hover:shadow-lg transition-shadow border border-gray-100">
                  {instructor.photo ? (
                    <div className="w-full aspect-[4/3] overflow-hidden bg-gray-200">
                      <img
                        src={instructor.photo}
                        alt={instructor.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="w-full aspect-[4/3] bg-blue-100 flex items-center justify-center">
                      <span className="text-5xl font-bold text-blue-600">
                        {instructor.name?.[0]}
                      </span>
                    </div>
                  )}
                  <div className="p-4 text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {instructor.name}
                    </h3>
                    {instructor.role && (
                      <p className="text-sm text-blue-600 font-medium mt-1">
                        {instructor.role}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      {instructor.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Your Tech Journey?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of young Africans who are already building their
            future with YATA. Registration is now open!
          </p>
          <a
            href={REGISTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg text-lg transition-colors inline-block"
          >
            Register Now
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
